import { apiClient } from './client';
import { extractBlobErrorMessage } from './payments';
import { PaginatedResponse } from '../types/pagination';
import {
  Alumni,
  AlumniDuplicate,
  AlumniFields,
  AlumniInteraction,
  AlumniInteractionType,
  AlumniListParams,
  AlumniLookups,
  AlumniStats,
  AlumniTimelineEvent,
  GraduationPreview,
  ImportParseResult,
  ImportPreview,
  ImportResult,
  StudentCandidate,
} from '../types/alumni';

/** Alumni Management (Phase 105) — /api/alumni. */

const clean = <T extends object>(params: T) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null));

export const listAlumni = async (params: AlumniListParams): Promise<PaginatedResponse<Alumni>> => {
  const { data } = await apiClient.get('/alumni', { params: clean(params) });
  return data;
};

export const getAlumniStats = async (branchId?: number): Promise<AlumniStats> => {
  const { data } = await apiClient.get('/alumni/stats', { params: clean({ branchId }) });
  return data.data;
};

export const getAlumniLookups = async (): Promise<AlumniLookups> => {
  const { data } = await apiClient.get('/alumni/lookups');
  return data.data;
};

export const getAlumni = async (id: number): Promise<Alumni> => {
  const { data } = await apiClient.get(`/alumni/${id}`);
  return data.data;
};

export type CreateAlumniPayload = Partial<AlumniFields> & {
  fullName: string;
  phone: string;
  studentId?: number;
  branchId?: number;
  allowDuplicate?: boolean;
};

export const createAlumni = async (payload: CreateAlumniPayload): Promise<Alumni> => {
  const { data } = await apiClient.post('/alumni', payload);
  return data.data;
};

export const updateAlumni = async (id: number, payload: Partial<AlumniFields> & { studentId?: number }): Promise<Alumni> => {
  const { data } = await apiClient.patch(`/alumni/${id}`, payload);
  return data.data;
};

export const archiveAlumni = async (id: number, reason: string): Promise<Alumni> => {
  const { data } = await apiClient.post(`/alumni/${id}/archive`, { reason });
  return data.data;
};

export const restoreAlumni = async (id: number): Promise<Alumni> => {
  const { data } = await apiClient.post(`/alumni/${id}/restore`);
  return data.data;
};

export const findAlumniDuplicates = async (probe: {
  email?: string;
  phone?: string;
  studentNumber?: string;
  excludeId?: number;
}): Promise<AlumniDuplicate[]> => {
  const { data } = await apiClient.get('/alumni/duplicates', { params: clean(probe) });
  return data.data;
};

export const findStudentCandidates = async (search: string): Promise<StudentCandidate[]> => {
  const { data } = await apiClient.get('/alumni/student-candidates', { params: { search } });
  return data.data;
};

export const listAlumniInteractions = async (id: number): Promise<AlumniInteraction[]> => {
  const { data } = await apiClient.get(`/alumni/${id}/interactions`);
  return data.data;
};

export const addAlumniInteraction = async (
  id: number,
  payload: { type: AlumniInteractionType; summary: string; occurredAt?: string; nextFollowUpAt?: string | null },
): Promise<AlumniInteraction> => {
  const { data } = await apiClient.post(`/alumni/${id}/interactions`, payload);
  return data.data;
};

export const getAlumniTimeline = async (id: number): Promise<AlumniTimelineEvent[]> => {
  const { data } = await apiClient.get(`/alumni/${id}/timeline`);
  return data.data;
};

export const getGraduationPreview = async (studentId: number): Promise<GraduationPreview> => {
  const { data } = await apiClient.get(`/alumni/graduation/${studentId}`);
  return data.data;
};

export const graduateStudent = async (
  studentId: number,
  payload: { graduatedAt: string; linkAlumniId?: number; alumni?: Partial<AlumniFields> },
): Promise<Alumni> => {
  const { data } = await apiClient.post(`/alumni/graduation/${studentId}`, payload);
  return data.data;
};

export const parseAlumniImport = async (file: File): Promise<ImportParseResult> => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post('/alumni/import/parse', form);
  return data.data;
};

export interface ImportPayload {
  headers: string[];
  rows: string[][];
  mapping: Record<string, string>;
  branchId?: number;
}

export const previewAlumniImport = async (payload: ImportPayload): Promise<ImportPreview> => {
  const { data } = await apiClient.post('/alumni/import/preview', payload);
  return data.data;
};

export const commitAlumniImport = async (payload: ImportPayload & { includeDuplicateRows: number[] }): Promise<ImportResult> => {
  const { data } = await apiClient.post('/alumni/import/commit', payload);
  return data.data;
};

export const exportAlumni = async (
  params: AlumniListParams & { format: 'xlsx' | 'pdf' | 'csv'; columns: string[] },
): Promise<{ blob: Blob; filename: string }> => {
  const { columns, page: _p, pageSize: _s, ...rest } = params;
  try {
    const response = await apiClient.get('/alumni/export', {
      params: clean({ ...rest, columns: columns.join(',') }),
      responseType: 'blob',
    });
    const disposition = (response.headers['content-disposition'] as string | undefined) ?? '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    return { blob: response.data, filename: match?.[1] ?? `alumni.${params.format}` };
  } catch (err) {
    throw new Error(await extractBlobErrorMessage(err, "Couldn't export the alumni list"));
  }
};
